import LegalLayout from '@/components/feature/LegalLayout';
import { useTranslation } from 'react-i18next';

export default function UserAgreement() {
  const { t } = useTranslation();
  return (
    <LegalLayout title={t('agreement.title', 'User Agreement')} lastUpdated="May 20, 2026">
      <div className="space-y-8">
        <section>
          <h2 className="text-lg font-semibold mb-3">{t('agreement.s1', '1. Agreement Overview')}</h2>
          <p className="leading-relaxed">
            This User Agreement (&quot;Agreement&quot;) is entered into between you (&quot;User&quot;, &quot;you&quot;, or &quot;your&quot;) and ZhiSu Inc. (&quot;ZhiSu&quot;, &quot;we&quot;, &quot;us&quot;, or &quot;our&quot;). This Agreement, together with our Terms of Service, Privacy Policy, Risk Disclosure, and any other policies referenced herein, governs your access to and use of the ZhiSu platform and related Services. By accessing or using our Services, you confirm that you have read, understood, and agree to be bound by this Agreement.
          </p>
        </section>

        <section>
          <h2 className="text-lg font-semibold mb-3">{t('agreement.s2', '2. Definitions')}</h2>
          <p className="leading-relaxed mb-3">
            For the purposes of this Agreement, the following terms shall have the meanings set forth below:
          </p>
          <ul className="list-disc pl-5 space-y-2 leading-relaxed">
            <li><strong>&quot;Platform&quot;</strong> means the ZhiSu decentralized trading platform, including all associated websites, applications, APIs, and smart contracts.</li>
            <li><strong>&quot;Services&quot;</strong> means all products, features, and functionalities offered through the Platform.</li>
            <li><strong>&quot;Wallet&quot;</strong> means a blockchain-based digital wallet that you control and use to interact with the Platform.</li>
            <li><strong>&quot;Assets&quot;</strong> means any cryptocurrency, tokens, or other digital assets deposited or held in connection with your use of the Services.</li>
            <li><strong>&quot;Trading&quot;</strong> means the buying, selling, or exchanging of perpetual futures contracts and other financial instruments available on the Platform.</li>
          </ul>
        </section>

        <section>
          <h2 className="text-lg font-semibold mb-3">{t('agreement.s3', '3. Account and Wallet Connection')}</h2>
          <p className="leading-relaxed mb-3">
            To access the Services, you must connect a compatible blockchain wallet. By connecting your wallet:
          </p>
          <ul className="list-disc pl-5 space-y-2 leading-relaxed">
            <li>You represent that you are the lawful owner of the wallet and all associated assets.</li>
            <li>You agree to maintain the security of your private keys and seed phrases.</li>
            <li>You understand that we do not custody your assets and cannot recover lost private keys.</li>
            <li>You are responsible for all transactions initiated from your connected wallet address.</li>
            <li>You may disconnect your wallet at any time, though open positions and pending orders may remain active.</li>
          </ul>
        </section>

        <section>
          <h2 className="text-lg font-semibold mb-3">{t('agreement.s4', '4. Trading Rules')}</h2>
          <p className="leading-relaxed mb-3">
            When using our trading Services, you agree to comply with the following rules:
          </p>
          <ul className="list-disc pl-5 space-y-2 leading-relaxed">
            <li>All orders submitted through the Platform are binding and irrevocable once executed on the blockchain.</li>
            <li>You are responsible for verifying all order details, including instrument, size, price, and leverage, before confirmation.</li>
            <li>Leveraged trading is subject to margin requirements, and positions may be liquidated if margin levels fall below maintenance thresholds.</li>
            <li>Funding payments for perpetual contracts are calculated and applied according to the protocol's funding rate mechanism.</li>
            <li>Market orders are executed at the best available price, which may differ from the last traded price displayed.</li>
            <li>In extreme market conditions, the protocol may implement circuit breakers or other protective measures.</li>
          </ul>
        </section>

        <section>
          <h2 className="text-lg font-semibold mb-3">{t('agreement.s5', '5. Fees and Payments')}</h2>
          <p className="leading-relaxed mb-3">
            You agree to pay all applicable fees as described on our Fees page and as updated from time to time. Fees include but are not limited to:
          </p>
          <ul className="list-disc pl-5 space-y-2 leading-relaxed">
            <li>Trading fees (maker and taker) calculated as a percentage of notional trade value</li>
            <li>Funding fees for open perpetual positions, calculated and exchanged between long and short holders</li>
            <li>Blockchain network fees (gas fees) for on-chain transactions, which are determined by network conditions</li>
            <li>Withdrawal fees for certain asset transfers</li>
            <li>Premium subscription fees for advanced AI agents or data services, where applicable</li>
          </ul>
          <p className="leading-relaxed mt-3">
            All fees are non-refundable except in cases of demonstrable platform error. We reserve the right to modify fee structures with reasonable notice.
          </p>
        </section>

        <section>
          <h2 className="text-lg font-semibold mb-3">{t('agreement.s6', '6. AI Agents and Automated Services')}</h2>
          <p className="leading-relaxed mb-3">
            If you choose to use AI trading agents or other automated services:
          </p>
          <ul className="list-disc pl-5 space-y-2 leading-relaxed">
            <li>You understand that AI agents operate autonomously based on pre-programmed algorithms and may execute trades without additional human confirmation.</li>
            <li>You retain full responsibility for all trades executed by AI agents connected to your account.</li>
            <li>We do not guarantee the performance, profitability, or risk-adjusted returns of any AI agent.</li>
            <li>You may adjust or disable AI agents at any time, though pending orders may still execute.</li>
            <li>Performance data displayed for AI agents is historical and may not reflect future results.</li>
          </ul>
        </section>

        <section>
          <h2 className="text-lg font-semibold mb-3">{t('agreement.s7', '7. API Usage')}</h2>
          <p className="leading-relaxed mb-3">
            If you access our Services through APIs, you agree to:
          </p>
          <ul className="list-disc pl-5 space-y-2 leading-relaxed">
            <li>Comply with our API documentation and rate limits</li>
            <li>Use API keys securely and not share them with unauthorized parties</li>
            <li>Not use the API for market manipulation, data scraping, or other prohibited activities</li>
            <li>Accept that API access may be modified, suspended, or terminated at our discretion</li>
            <li>Be responsible for any actions taken using your API credentials</li>
          </ul>
        </section>

        <section>
          <h2 className="text-lg font-semibold mb-3">{t('agreement.s8', '8. Representations and Warranties')}</h2>
          <p className="leading-relaxed mb-3">
            You represent and warrant that:
          </p>
          <ul className="list-disc pl-5 space-y-2 leading-relaxed">
            <li>You have the legal capacity to enter into this Agreement and use the Services.</li>
            <li>All information you provide is accurate, complete, and current.</li>
            <li>You are not subject to any sanctions or located in any prohibited jurisdiction.</li>
            <li>You understand the risks associated with cryptocurrency trading and blockchain technology.</li>
            <li>You are using the Services for your own account and not on behalf of any third party, unless explicitly authorized.</li>
            <li>You will comply with all applicable tax obligations arising from your use of the Services.</li>
          </ul>
        </section>

        <section>
          <h2 className="text-lg font-semibold mb-3">{t('agreement.s9', '9. Termination')}</h2>
          <p className="leading-relaxed mb-3">
            This Agreement remains in effect until terminated. We may suspend or terminate your access to the Services immediately and without notice if:
          </p>
          <ul className="list-disc pl-5 space-y-2 leading-relaxed mb-3">
            <li>You violate any provision of this Agreement or any applicable law</li>
            <li>We reasonably suspect fraudulent, illegal, or harmful activity</li>
            <li>We are required to do so by law, regulation, or court order</li>
            <li>We discontinue the Services or a portion thereof</li>
          </ul>
          <p className="leading-relaxed">
            Upon termination, your right to use the Services ceases immediately. Provisions that by their nature should survive termination shall survive, including but not limited to indemnification, limitation of liability, and dispute resolution provisions.
          </p>
        </section>

        <section>
          <h2 className="text-lg font-semibold mb-3">{t('agreement.s10', '10. Dispute Resolution')}</h2>
          <p className="leading-relaxed mb-3">
            Any dispute arising out of or relating to this Agreement shall be resolved as follows:
          </p>
          <ul className="list-disc pl-5 space-y-2 leading-relaxed">
            <li><strong>Informal Resolution:</strong> Parties shall first attempt to resolve disputes informally through good faith negotiation for a period of at least 30 days.</li>
            <li><strong>Arbitration:</strong> If informal resolution fails, disputes shall be submitted to binding arbitration administered by the London Court of International Arbitration (LCIA) under its Arbitration Rules.</li>
            <li><strong>Class Action Waiver:</strong> You agree to resolve disputes on an individual basis and waive any right to participate in class actions, collective proceedings, or representative actions.</li>
            <li><strong>Governing Law:</strong> This Agreement is governed by the laws of the British Virgin Islands, without regard to conflict of law principles.</li>
          </ul>
        </section>

        <section>
          <h2 className="text-lg font-semibold mb-3">{t('agreement.s11', '11. Modifications')}</h2>
          <p className="leading-relaxed">
            We reserve the right to modify this Agreement at any time. Material changes will be notified through the Platform or via email. Your continued use of the Services after changes take effect constitutes acceptance of the revised Agreement. It is your responsibility to review this Agreement periodically.
          </p>
        </section>

        <section>
          <h2 className="text-lg font-semibold mb-3">{t('agreement.s12', '12. Entire Agreement')}</h2>
          <p className="leading-relaxed">
            This Agreement, together with the documents referenced herein, constitutes the entire agreement between you and ZhiSu regarding the subject matter hereof and supersedes all prior or contemporaneous understandings, whether written or oral. If any provision of this Agreement is found to be invalid or unenforceable, the remaining provisions shall continue in full force and effect.
          </p>
        </section>

        <section>
          <h2 className="text-lg font-semibold mb-3">{t('agreement.s13', '13. Contact')}</h2>
          <p className="leading-relaxed">
            For questions about this User Agreement, please contact us at legal@zhisu.trade or through our Support Center at support.zhisu.trade.
          </p>
        </section>
      </div>
    </LegalLayout>
  );
}